import React, { useEffect, useState } from "react";
import { Wallet, Loader2, X } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

/** Ein Klick: Beleg als Spesen eines Mitarbeitenden markieren (privat bezahlt → Aufwand an 2060, Rückerstattung im Lohnlauf). */
export function ExpenseClaimAction({ companyId, item, onUpdated, compact = false }) {
  const [employees, setEmployees] = useState(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const claim = item.source_meta?.expense_claim;

  useEffect(() => {
    if (!picking || employees) return;
    apiClient.get(`/companies/${companyId}/employees`).then((r) => setEmployees((r.data || []).filter((e) => !e.archived_at))).catch(() => setEmployees([]));
  }, [picking, employees, companyId]);

  const apply = async (employeeId) => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/expense-claim`, { employee_id: employeeId });
      onUpdated?.({ ...item, checks: r.data.checks, routing: r.data.routing, source_meta: r.data.source_meta, extracted: r.data.extracted, kind: r.data.kind });
      const n = r.data.applied_to_pending || 0;
      toast.success(employeeId ? `Als Spesen von ${r.data.expense_claim?.employee_name} erfasst – Aufwand an 2060, Rückerstattung im nächsten Lohnlauf` : "Spesen-Markierung entfernt", {
        duration: 7000,
        description: employeeId && r.data.card_saved ? `Karte ${r.data.card_saved.brand || ""} …${r.data.card_saved.last4} als Privatkarte gemerkt – künftige Belege werden automatisch als Spesen erkannt${n ? ` (jetzt schon auf ${n} weitere offene Belege angewendet)` : ""}` : undefined,
      });
      setPicking(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Spesen konnten nicht gesetzt werden"); } finally { setBusy(false); }
  };

  if (claim) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-teal-50 border border-teal-200 px-2 py-0.5 text-xs text-teal-800" data-testid={`expense-claim-badge-${item.id}`}>
        <Wallet className="h-3 w-3" /> Spesen – Rückerstattung an {claim.employee_name}
        <button type="button" onClick={() => apply(null)} disabled={busy} className="ml-1 rounded hover:bg-teal-100 p-px" title="Spesen-Markierung entfernen" data-testid={`expense-claim-remove-${item.id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
        </button>
      </span>
    );
  }
  if (!picking) {
    return (
      <button type="button" onClick={() => setPicking(true)}
        className={`inline-flex items-center gap-1 rounded border border-teal-400 bg-teal-600 text-white px-2 py-0.5 font-medium hover:bg-teal-700 ${compact ? "text-[11px]" : "text-xs"}`}
        data-testid={`expense-claim-button-${item.id}`}>
        <Wallet className="h-3 w-3" /> Als Spesen des Mitarbeiters erfassen
      </button>
    );
  }
  return (
    <div className="w-full rounded border border-teal-300 bg-white p-2 text-xs space-y-1.5" data-testid={`expense-claim-picker-${item.id}`}>
      <div className="font-medium text-teal-900">Wer hat privat bezahlt?</div>
      {!employees ? <Loader2 className="h-4 w-4 animate-spin text-teal-600" /> : employees.length === 0 ? (
        <div className="text-slate-500">Keine Mitarbeitenden erfasst – zuerst unter Lohn → Mitarbeitende anlegen.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {employees.map((e) => (
            <button key={e._id || e.id} type="button" disabled={busy} onClick={() => apply(e._id || e.id)}
              className="rounded-full border border-teal-300 bg-teal-50 px-2.5 py-1 text-teal-900 hover:bg-teal-100" data-testid={`expense-claim-employee-${e._id}`}>
              {e.first_name} {e.last_name}
            </button>
          ))}
        </div>
      )}
      <button type="button" onClick={() => setPicking(false)} className="text-slate-500 hover:underline" data-testid={`expense-claim-cancel-${item.id}`}>Abbrechen</button>
    </div>
  );
}
